import { Form, Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import { Button, ButtonTypes } from '@/components/Button';
import { Head } from '@/components/Head';
import { InputSelect } from '@/components/form/InputSelect';
import { useTrackPersonalization } from '@/modules/analytic/hooks/useTrackPersonalization';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useNotifications } from '@/modules/notifications/hooks/useNotifications';
import { usePersonalization } from '@/modules/onboarding/hooks/usePersonalization';
import { OnboardingCategorySection } from '@/modules/onboarding/presentation/OnboardingCategorySection';

const PersonalizationPage = () => {
  const [hasReset, setHasReset] = useState(false);
  const { showNotificationError, showNotificationInfo } = useNotifications();
  const { user } = useAuth();
  const { trackPersonalization } = useTrackPersonalization();
  const { formatMessage } = useIntl();
  const {
    categories,
    jobCategories,
    isLoadingCategories,
    isLoadingJobCategories,
    updateUserAsync,
  } = usePersonalization();

  const validationSchema = Yup.object().shape({
    corpoJobCategory: user?.isCorporate ? Yup.string().required() : null,
    categories: Yup.array().required().min(2),
  });

  const moveJobCategoryInCategories = ({
    corpoJobCategory,
    categories,
  }: {
    corpoJobCategory: string;
    categories: string[];
  }) => {
    return [...categories.filter((category) => !category.includes('jobcat/')), corpoJobCategory];
  };

  const onSubmit = async ({
    corpoJobCategory,
    categories,
  }: {
    corpoJobCategory: string;
    categories: string[];
  }) => {
    if (!user?.UUID || isLoadingCategories || isLoadingJobCategories) return;

    try {
      await updateUserAsync({
        userId: user.UUID,
        data: {
          categories: moveJobCategoryInCategories({ categories, corpoJobCategory }).filter(
            (cat) => !!cat
          ),
        },
      });

      trackPersonalization({
        corpoJobCategory,
        categories,
        hasReset,
      });

      showNotificationInfo('onboarding.categories.success.notification');
    } catch (error) {
      showNotificationError('onboarding.categories.error.notification');
    }
  };

  const initialValues = useMemo(
    () => ({
      categories: user?.categories.filter((category) => !category.includes('jobcat/')) || [],
      corpoJobCategory: user?.corpoJobCategory || '',
    }),
    [user]
  );

  const handleReset = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
    ) => {
      setFieldValue('categories', []);
      setFieldValue('corpoJobCategory', '');
      setHasReset(true);
    },
    []
  );

  const jobCategoriesOptions = useMemo(
    () => [
      ...(jobCategories?.[0]?.items || []).map(({ title, user_tag }) => ({
        text: title,
        value: user_tag,
      })),
    ],
    [jobCategories]
  );

  const isLoading = isLoadingCategories || isLoadingJobCategories;

  return (
    <div className="content">
      <Head metas={{ title: formatMessage({ id: 'metas.page.personalization' }) }} />
      <div className="p-5">
        {isLoading ? null : (
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            validateOnMount
            validateOnChange
            initialStatus="form"
          >
            {({
              isValid,
              values,
              setFieldValue,
              validateForm,
              handleSubmit,
              isSubmitting,
              dirty,
            }) => {
              return (
                <Form className="block w-full max-w-4xl mx-auto">
                  <div className="sticky top-14 z-20 pb-10">
                    <div className="w-full flex justify-between items-center bg-brand-neutral-950">
                      <h1 className="title-h2">
                        {formatMessage({
                          id: 'personalization.header.title',
                        })}
                      </h1>
                      <div className="flex gap-2">
                        <Button
                          style="link"
                          onClick={() => {
                            handleReset(setFieldValue);
                            validateForm(false);
                          }}
                        >
                          {formatMessage({ id: 'global.reset' })}
                        </Button>
                        <Button
                          style="primary"
                          type={ButtonTypes.Button}
                          onClick={handleSubmit}
                          disabled={!dirty || !isValid || isSubmitting}
                        >
                          {formatMessage({
                            id: `global.save`,
                          })}
                        </Button>
                      </div>
                    </div>
                    <div className="w-full scrollable-top-dark-shadow" />
                  </div>
                  <div>
                    {user?.isCorporate && (
                      <div className="mb-10">
                        <h3 className="title-h4 mb-1">
                          {formatMessage({
                            id: 'onboarding.jobCategories.header.title',
                          })}
                        </h3>
                        <p className="text-brand-neutral-300 text-sm">
                          {formatMessage({
                            id: 'onboarding.jobCategories.header.description',
                          })}
                        </p>

                        <div className="w-full max-w-sm my-6 2xl:text-base text-left">
                          <InputSelect
                            name="corpoJobCategory"
                            options={jobCategoriesOptions}
                            value={values.corpoJobCategory}
                          />
                        </div>
                      </div>
                    )}
                    <h3 className="title-h4 mb-1">
                      {formatMessage({
                        id: 'onboarding.categories.header.title',
                      })}
                    </h3>
                    <p className="text-brand-neutral-300 text-sm">
                      {formatMessage({
                        id: 'onboarding.categories.header.description',
                      })}
                    </p>
                    <div>
                      {categories.map((category, index) => (
                        <OnboardingCategorySection
                          fieldName="categories"
                          category={category}
                          key={`categorie-onboarding-${index}`}
                          value={values.categories}
                          multiple
                        />
                      ))}
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default PersonalizationPage;
